import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { DatePickerFr } from '../ui/DatePickerFr';
import { Building, Calendar, Edit, Loader2, MapPin, Phone, Save, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { formatDate, getExpirationDate, BACKEND_URL } from './constants';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { DateEffetButton } from '../dateEffet/DateEffetDialog';

const getStatusBadge = (status) => {
  const statusConfig = {
    active: { label: 'Active', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    completed: { label: 'Complete', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    pending: { label: 'En attente', className: 'bg-blue-100 text-blue-800 border-blue-200' },
    draft: { label: 'Brouillon', className: 'bg-gray-100 text-gray-800 border-gray-200' },
    signed: { label: 'Signee', className: 'bg-blue-100 text-blue-800 border-blue-200' },
    expired: { label: 'Expiree', className: 'bg-red-100 text-red-800 border-red-200' },
  };
  const config = statusConfig[status] || statusConfig.pending;
  return <Badge className={config.className}>{config.label}</Badge>;
};

function toInputDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  } catch { return ''; }
}

export default function AdhesionDetailModal({ adhesion, open, onOpenChange, onUpdated }) {
  const { getAuthHeader, user } = useAuth();
  const superAdmin = user?.role === 'super_admin';
  const [editingDates, setEditingDates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dateAdhesion, setDateAdhesion] = useState('');
  const [dateExpiration, setDateExpiration] = useState('');

  if (!adhesion) return null;

  const startEditDates = () => {
    setDateAdhesion(toInputDate(adhesion.created_at || adhesion.date_creation));
    setDateExpiration(toInputDate(adhesion.expiration_date));
    setEditingDates(true);
  };

  const cancelEdit = () => {
    setEditingDates(false);
  };

  const saveDates = async () => {
    setSaving(true);
    try {
      const body = {};
      if (dateAdhesion) body.date_adhesion = new Date(dateAdhesion).toISOString();
      if (dateExpiration) body.date_expiration = new Date(dateExpiration).toISOString();

      await axios.patch(
        `${BACKEND_URL}/api/ftpe-adhesion/admin/${adhesion.id}/dates`,
        body,
        { headers: getAuthHeader() }
      );
      toast.success('Dates mises a jour avec succes');
      setEditingDates(false);
      if (onUpdated) onUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise a jour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building className="w-5 h-5 text-blue-600" />
            Details de l&apos;adhesion FTPE
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Informations entreprise */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Building className="w-4 h-4" />
              Entreprise
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Raison sociale:</span>
                <p className="font-medium">{adhesion.raison_sociale || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">SIRET:</span>
                <p className="font-medium">{adhesion.siret || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Forme juridique:</span>
                <p className="font-medium">{adhesion.forme_juridique || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Activite:</span>
                <p className="font-medium">{adhesion.activite_principale || '-'}</p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-blue-50 p-4 rounded-lg space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Contact
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Representant:</span>
                <p className="font-medium">{adhesion.representant_nom || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Qualite:</span>
                <p className="font-medium">{adhesion.representant_qualite || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Email:</span>
                <p className="font-medium">{adhesion.email || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Telephone:</span>
                <p className="font-medium">{adhesion.telephone || '-'}</p>
              </div>
            </div>
          </div>

          {/* Localisation */}
          <div className="bg-green-50 p-4 rounded-lg space-y-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Localisation
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Territoire:</span>
                <p className="font-medium">{adhesion.territoire || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Adresse:</span>
                <p className="font-medium">{adhesion.adresse || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Code postal:</span>
                <p className="font-medium">{adhesion.code_postal || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Ville:</span>
                <p className="font-medium">{adhesion.ville || '-'}</p>
              </div>
            </div>
          </div>

          {/* Statut adhesion + Dates editables */}
          <div className="bg-amber-50 p-4 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Dates et Statut
              </h3>
              {superAdmin && !editingDates ? (
                <DateEffetButton produit="ftpe" ident={adhesion.id} onDone={() => { if (onUpdated) onUpdated(); onOpenChange(false); }} testId="ftpe-date-effet-btn" />
              ) : !editingDates ? (
                <Button variant="outline" size="sm" onClick={startEditDates} className="gap-1.5 text-xs" data-testid="edit-dates-btn">
                  <Edit className="w-3.5 h-3.5" /> Modifier les dates
                </Button>
              ) : (
                <div className="flex gap-1.5">
                  <Button variant="outline" size="sm" onClick={cancelEdit} className="gap-1 text-xs">
                    <X className="w-3.5 h-3.5" /> Annuler
                  </Button>
                  <Button size="sm" onClick={saveDates} disabled={saving} className="gap-1 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="save-dates-btn">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Enregistrer
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Statut:</span>
                <div className="mt-1">{getStatusBadge(adhesion.status)}</div>
              </div>
              <div>
                <span className="text-gray-500">Mode de paiement:</span>
                <p className="font-medium">
                  {adhesion.payment_method === 'stripe' && <span className="text-purple-700">Carte bancaire (Stripe)</span>}
                  {adhesion.payment_method === 'virement' && <span className="text-blue-700">Virement bancaire</span>}
                  {adhesion.payment_method === 'manual_activation' && <span className="text-green-700">Activation manuelle</span>}
                  {!adhesion.payment_method && <span className="text-gray-500">Non renseigne</span>}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Date adhesion:</span>
                {editingDates ? (
                  <DatePickerFr value={dateAdhesion} onChange={setDateAdhesion}
                    className="mt-1 h-8 text-sm" id="input-date-adhesion" />
                ) : (
                  <p className="font-medium">{formatDate(adhesion.created_at || adhesion.date_creation)}</p>
                )}
              </div>
              <div>
                <span className="text-gray-500">Date expiration:</span>
                {editingDates ? (
                  <DatePickerFr value={dateExpiration} onChange={setDateExpiration}
                    className="mt-1 h-8 text-sm" id="input-date-expiration" />
                ) : (
                  <p className="font-medium">{getExpirationDate(adhesion)}</p>
                )}
              </div>
              <div>
                <span className="text-gray-500">Statut paiement:</span>
                <p className="font-medium">
                  {adhesion.payment_status === 'paid' && <span className="text-green-700">Paye</span>}
                  {adhesion.payment_status === 'pending' && <span className="text-orange-600">En attente</span>}
                  {adhesion.payment_status === 'pending_virement' && <span className="text-blue-600">Virement en attente</span>}
                  {adhesion.payment_status === 'failed' && <span className="text-red-600">Echoue</span>}
                  {!adhesion.payment_status && adhesion.status === 'completed' && <span className="text-green-700">Valide</span>}
                  {!adhesion.payment_status && adhesion.status !== 'completed' && <span className="text-gray-500">Non renseigne</span>}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Date paiement:</span>
                <p className="font-medium">{adhesion.paid_at ? formatDate(adhesion.paid_at) : '-'}</p>
              </div>
              {adhesion.reference_virement && (
                <div className="col-span-2">
                  <span className="text-gray-500">Reference virement:</span>
                  <p className="font-mono font-medium text-blue-700">{adhesion.reference_virement}</p>
                </div>
              )}
            </div>
          </div>

          {/* ID technique */}
          <div className="text-xs text-gray-400 text-center">
            ID: {adhesion.id || adhesion._id}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
